import classnames from "classnames";
import { isLeft } from "fp-ts/lib/Either";
import { inject } from "mobx-react";

import {
  CoastArticlePayloadResolver,
  formatImage,
  getPaths,
  ILog,
  matchPrefix,
  removePrefix,
} from "@ihr-radioedit/inferno-core";
import { FeedResultFragment } from "@ihr-radioedit/inferno-webapi";
import type { Store } from "@inferno/renderer-shared-core";
import { COAST_CATEGORIES_MAP, isPublishRecord, RESPONSIVE_IMAGE, reverseRoute } from "@inferno/renderer-shared-core";
import { DateDisplay } from "../../../../core/components/DateDisplay.component";
import { ResponsiveImage } from "../../../../core/components/ResponsiveImage.component";
import { trimText } from "../../../../core/lib/utilities";
import { MagicLink } from "../../../../core/ui";
import { CoastFeedItemType, getCoastFeedImg } from "../../lib/coastFeed";

const log = ILog.logger("CoastFeedArticleItem.component");

interface CoastFeedArticleItemProps {
  item: FeedResultFragment;
  layout?: "first-item" | "default";
  store?: Store;
}

export const CoastFeedArticleItem = inject("store")(
  ({ item, layout = "default", store }: CoastFeedArticleItemProps) => {
    if (!store || !isPublishRecord(item.record)) {
      return null;
    }
    const { slug, pub_start } = item.record;
    const resolvedPayload = CoastArticlePayloadResolver.decode(item.record.payload);

    if (isLeft(resolvedPayload)) {
      log.debug(`Payload failed validation: ${getPaths(resolvedPayload)}`);
      return null;
    }

    const payload = resolvedPayload.right;
    const {
      summary,
      targeting: { targets },
    } = payload;

    const articleUrl = reverseRoute(store.site, "articles_detail", { slug }) || "#";

    const altText = summary.image.alt_text || summary.title || "";
    const thumb = formatImage(summary.image.url, store.env.IMAGE_HOST);
    const { imgSrc, srcset } = getCoastFeedImg(thumb, CoastFeedItemType.ARTICLE, { quality: 80 });

    const itemCss = classnames("coast-feed-item", {
      [`${layout}`]: layout !== "default",
    });

    const categories: { slug: string; displayName: string }[] = [];
    targets.forEach(target => {
      target.what.filter(matchPrefix(/^coast-categories\//)).forEach(category => {
        const categorySlug = removePrefix(/^coast-categories\//)(category);
        const result = COAST_CATEGORIES_MAP[categorySlug];
        if (result) {
          categories.push({ slug: categorySlug, displayName: result });
        }
      });
    });

    return (
      <div className={itemCss}>
        <figure className="item-thumb">
          <MagicLink to={articleUrl} context={slug}>
            <ResponsiveImage
              src={imgSrc}
              srcset={srcset}
              alt={altText}
              initialWidth={RESPONSIVE_IMAGE.responsiveImageInitialWidth}
              initialHeight={RESPONSIVE_IMAGE.responsiveImageInitialHeight}
            />
          </MagicLink>
        </figure>
        <DateDisplay datetime={pub_start} className="item-published" dateFormat="MMM dd yyyy" />
        <MagicLink className="item-title" to={articleUrl} context={slug}>
          {summary.title}
        </MagicLink>

        {layout === "default" ? (
          <section className="item-summary">{trimText(summary.description || "", 250)}</section>
        ) : null}

        <section className="item-categories">
          {categories.map((category, index) => (
            <MagicLink key={index} to={`/articles/${category.slug}`}>
              {category.displayName}
            </MagicLink>
          ))}
        </section>
      </div>
    );
  },
);

export default CoastFeedArticleItem;
